/**
 * `HighlightMark` — a minimal `<mark>` inline mark for the Tiptap rich pane.
 *
 * Why hand-rolled instead of `@tiptap/extension-highlight` (2026-05-21):
 *
 * The official `@tiptap/extension-highlight` package is NOT a dependency of
 * this frontend (see package.json — only `@tiptap/extension-color` and
 * `@tiptap/extension-text-style` are present), and the editor-upgrade task
 * forbids adding new deps. This ~30-line mark gives the same `<mark>`-based
 * highlight behaviour — toggle on/off via `toggleHighlight()` — using only
 * `@tiptap/core`, which is already installed.
 *
 * Round-trip note (markdownRoundtrip.ts):
 *
 *   The mark serialises to `<mark>…</mark>` on the way out (`editor.getHTML()`),
 *   and turndown is configured to KEEP `<mark>` as raw inline HTML so the
 *   highlight survives the markdown round-trip — markdown has no native
 *   highlight syntax, so raw inline HTML is the canonical representation, the
 *   same approach the existing colored-`<span>` preservation uses.
 */

import { Mark, mergeAttributes } from '@tiptap/core';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    highlightMark: {
      /** Toggle the `<mark>` highlight on the current selection. */
      toggleHighlight: () => ReturnType;
      /** Remove the highlight from the current selection. */
      unsetHighlight: () => ReturnType;
    };
  }
}

export const HighlightMark = Mark.create({
  name: 'highlight',

  parseHTML() {
    return [{ tag: 'mark' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['mark', mergeAttributes(HTMLAttributes), 0];
  },

  addCommands() {
    return {
      toggleHighlight:
        () =>
        ({ commands }) =>
          commands.toggleMark(this.name),
      unsetHighlight:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),
    };
  },
});
